import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import ListSearch from 'Components/ListSearch'
import PageTitle from 'Components/PageTitle'
import { DeviceList } from 'Services/SalesManagement/Features/WalttiPosRemoteConfig/Components/DeviceList'
import {
  type DeviceParamShape,
  DeviceType,
  type DeviceTypes,
  fetchDeviceConfigList
} from 'Services/SalesManagement/Features/WalttiPosRemoteConfig/walttiPosRemoteConfigSlice'
import useTypedSelector, { useAppDispatch } from 'Store'
import { SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import useQuery from 'Util/getQueryParamsHook'
import AddIcon from '@mui/icons-material/Add'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Tab } from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

export default function WalttiPosDeviceListPage() {
  const baseUrl = `${SALES_MANAGEMENT_ROOT_URL}/walttipos-remote-config`
  const dispatch = useAppDispatch()

  const queryParams = useQuery()
  const navigate = useNavigate()

  const { devices, count, loading } = useTypedSelector(state => state.walttiPosRemoteConfig)

  const [searchText, setSearchText] = useState(queryParams.get('search') ?? '')

  const defaultTab =
    (queryParams.get('type_configuration') as DeviceTypes) || DeviceType.walttiSelfServiceCheckout

  const [tabValue, setTabValue] = useState<DeviceTypes>(defaultTab)

  const [params, setParams] = useState<DeviceParamShape>({
    page: queryParams.get('page') ? parseInt(queryParams.get('page') as string) : 1,
    search: queryParams.get('search') ?? '',
    type_configuration: tabValue
  })

  useEffect(() => {
    dispatch(fetchDeviceConfigList(params)).then(() => {
      setQueryParams()
    })
  }, [dispatch, params])

  const setQueryParams = () => {
    const query: string[] = []
    Object.keys(params).forEach((key: string) => {
      const value = params[key as keyof DeviceParamShape]
      if (value) {
        query.push(key + '=' + value)
      }
    })
    const queryString = query.join('&')

    navigate({
      pathname: location.pathname,
      search: '?' + queryString
    })
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({ ...prevState, page: newPage + 1 }))
  }

  const handleSearch = () => {
    setParams(prevState => ({ ...prevState, search: searchText, page: 1 }))
  }

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: DeviceTypes) => {
    setParams(prevState => ({ ...prevState, type_configuration: newValue }))
    setTabValue(newValue)
  }

  const breadcrumbs = [
    {
      title: t`Home`,
      link: SALES_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Points of sale's remote configurations`
    }
  ]

  const rows = devices

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <BackButton link={SALES_MANAGEMENT_ROOT_URL} />
        <PageTitle>{t`Points of sale's remote configurations`}</PageTitle>
        <Typography component="p" variant="subtitle1" sx={{ fontSize: '15px', mb: 4 }}>
          {t`In the remote configurations section you can control points of sale's remote configurations by device.`}
        </Typography>
        <ListSearch
          searchText={searchText}
          handleSearchTextChange={handleSearchTextChange}
          handleSearch={handleSearch}
          placeholder={t`Search for points of sale's devices`}
          helperText={t`Search for point of sale's device name or machine ID`}
        />
      </Box>
      <TabContext value={tabValue}>
        <Paper>
          <TabList
            onChange={handleTabChange}
            aria-label={t`Points of sale's remote configurations tab list`}
          >
            <Tab
              label={t`Waltti self service checkouts`}
              value={DeviceType.walttiSelfServiceCheckout}
            />
            <Tab label={t`Waltti kiosks`} value={DeviceType.walttiKiosk} />
            <Tab
              label={t`Föli self service checkouts`}
              value={DeviceType.foliSelfServiceCheckout}
            />
            <Tab label={t`Föli kiosks`} value={DeviceType.foliKiosk} />
          </TabList>
        </Paper>
        <TabPanel sx={{ px: 0, pt: 1 }} value={DeviceType.walttiSelfServiceCheckout}>
          <CustomButton
            sx={{ mt: 1, mb: 2 }}
            onClick={() => navigate(`${baseUrl}/create/${DeviceType.walttiSelfServiceCheckout}`)}
            size="small"
            startIcon={<AddIcon />}
          >
            {t`Create new Waltti self service checkout configuration`}
          </CustomButton>
          <DeviceList
            rows={rows}
            loading={loading}
            params={params}
            handleChangePage={handleChangePage}
            count={count}
          />
        </TabPanel>
        <TabPanel sx={{ px: 0, pt: 1 }} value={DeviceType.walttiKiosk}>
          <CustomButton
            sx={{ mt: 1, mb: 2 }}
            onClick={() => navigate(`${baseUrl}/create/${DeviceType.walttiKiosk}`)}
            size="small"
            startIcon={<AddIcon />}
          >
            {t`Create new Waltti kiosk configuration`}
          </CustomButton>
          <DeviceList
            rows={rows}
            loading={loading}
            params={params}
            handleChangePage={handleChangePage}
            count={count}
          />
        </TabPanel>
        <TabPanel sx={{ px: 0, pt: 1 }} value={DeviceType.foliSelfServiceCheckout}>
          <CustomButton
            sx={{ mt: 1, mb: 2 }}
            onClick={() => navigate(`${baseUrl}/create/${DeviceType.foliSelfServiceCheckout}`)}
            size="small"
            startIcon={<AddIcon />}
          >
            {t`Create new Föli self service checkout configuration`}
          </CustomButton>
          <DeviceList
            rows={rows}
            loading={loading}
            params={params}
            handleChangePage={handleChangePage}
            count={count}
          />
        </TabPanel>
        <TabPanel sx={{ px: 0, pt: 1 }} value={DeviceType.foliKiosk}>
          <CustomButton
            sx={{ mt: 1, mb: 2 }}
            onClick={() => navigate(`${baseUrl}/create/${DeviceType.foliKiosk}`)}
            size="small"
            startIcon={<AddIcon />}
          >
            {t`Create new Föli kiosk configuration`}
          </CustomButton>
          <DeviceList
            rows={rows}
            loading={loading}
            params={params}
            handleChangePage={handleChangePage}
            count={count}
          />
        </TabPanel>
      </TabContext>
    </Box>
  )
}
